<section class="move">
	<lvr-heading>
		{{ 'user.assignment_transfer.title' | transloco }}
	</lvr-heading>

	<div class="move-controls">
		<div class="search">
			<div class="move-modes" role="radiogroup">
				<p>{{ 'user.assignment_transfer.search_by' | transloco }}</p>

				<lvr-button
					[variant]="mode() === 'client' ? 'secondary' : 'primary'"
					[label]="'user.assignment_transfer.client' | transloco"
					[type]="'button'"
					role="radio"
					[attr.aria-checked]="mode() === 'client'"
					(clicked)="setMode('client')"
					icon="person"
				></lvr-button>

				<lvr-button
					[variant]="mode() === 'owner' ? 'secondary' : 'primary'"
					[label]="'user.assignment_transfer.user' | transloco"
					[type]="'button'"
					role="radio"
					[attr.aria-checked]="mode() === 'owner'"
					(clicked)="setMode('owner')"
					icon="assignment"
				></lvr-button>

				<lvr-button
					[variant]="mode() === 'assignmentGuid' ? 'secondary' : 'primary'"
					[label]="'user.assignment_transfer.assignment_number' | transloco"
					[type]="'button'"
					role="radio"
					[attr.aria-checked]="mode() === 'assignmentGuid'"
					(clicked)="setMode('assignmentGuid')"
					icon="numbers"
				></lvr-button>
			</div>

			<lvr-field>
				<mat-form-field lvrInput>
					<input
						#searchInput
						type="text"
						matInput
						[placeholder]="searchPlaceholderKey() | transloco"
						(input)="updateTerm($event)"
					/>
				</mat-form-field>
			</lvr-field>
		</div>

		<div class="right-side">
			<lvr-field [label]="'user.assignment_transfer.date_move_hint' | transloco | appendAsterisk">
				<mat-form-field
					lvrInput
					[lvrError]="
						hasError(
							moveDateCtrl,
							'matDatepickerParse',
							'minDateExceeded',
							'maxDateExceeded',
							'matDatepickerMin',
							'matDatepickerMax',
							'required'
						)
					"
				>
					<input
						matInput
						[matDatepicker]="picker"
						[formControl]="moveDateCtrl"
						[min]="globalMinMoveDate()"
						[max]="globalMaxMoveDate()"
						placeholder="09.10.2025"
						[errorStateMatcher]="matcher"
					/>

					@if (hasError(moveDateCtrl, 'matDatepickerParse')) {
						<mat-error>
							{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}
						</mat-error>
					} @else if (hasError(moveDateCtrl, 'matDatepickerMin', 'minDateExceeded')) {
						<mat-error>
							{{
								'base.form.error.date_min_error' | transloco: { minDate: (globalMinMoveDate() | date: 'dd.MM.yyyy') }
							}}
						</mat-error>
					} @else if (hasError(moveDateCtrl, 'matDatepickerMax', 'maxDateExceeded')) {
						<mat-error>
							{{
								'base.form.error.date_max_error' | transloco: { maxDate: (globalMaxMoveDate() | date: 'dd.MM.yyyy') }
							}}
						</mat-error>
					} @else if (hasError(moveDateCtrl, 'required')) {
						<mat-error>
							{{ 'base.form.error.required' | transloco }}
						</mat-error>
					} @else {
						<mat-hint>{{ dateFormatHint }}</mat-hint>
					}

					<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
					<mat-datepicker #picker></mat-datepicker>
				</mat-form-field>
			</lvr-field>

			<div class="select-actions">
				<lvr-button
					variant="primary"
					[label]="'user.assignment_transfer.select_all' | transloco"
					[type]="'button'"
					[disabled]="clients().length === 0"
					[loading]="loading()"
					(clicked)="selectAllOnPage()"
					icon=""
				></lvr-button>

				<lvr-button
					variant="primary"
					[label]="'user.assignment_transfer.unselect_all' | transloco"
					[type]="'button'"
					[disabled]="selectedCount() === 0"
					[loading]="loading()"
					(clicked)="clearSelection()"
					icon=""
				></lvr-button>
			</div>
		</div>
	</div>

	@if (loading()) {
		<p class="searching">{{ 'global_search.searching' | transloco }}</p>
	}

	@if (!loading() && clients().length === 0) {
		<p class="searching">{{ 'global_search.no_results' | transloco: { term: term() } }}</p>
	}

	@if (!loading() && clients().length > 0) {
		<div class="move-list">
			@for (c of activeSectionClients(); track c.clientId) {
				<section class="client-block">
					<div class="client-header">
						<h3>{{ c.clientFullName }}</h3>
					</div>

					<div class="assignment-list">
						<lvr-user-client-assignment-item
							[clientId]="c.clientId"
							itemType="fds"
							variant="newStatus"
							[checked]="isFdsSelected(c.clientId)"
							[assignment]="null"
							[hideDate]="true"
							[hideNumber]="true"
							[ownerLabel]="''"
							(toggled)="onItemToggled($event)"
						/>

						@for (a of c.active; track a.id) {
							@if (isReOwnershipP0(a)) {
								<lvr-user-client-assignment-item
									[clientId]="c.clientId"
									[assignment]="a"
									variant="newStatus"
									[ownerLabel]="'user.assignment_transfer.previous_owner' | transloco"
									[hideDate]="true"
									[hideNumber]="true"
									[checked]="isSelected(c.clientId, a.id)"
									(toggled)="onItemToggled($event)"
								/>
							} @else {
								<lvr-user-client-assignment-item
									[clientId]="c.clientId"
									[assignment]="a"
									[checked]="isSelected(c.clientId, a.id)"
									(toggled)="onItemToggled($event)"
								/>
							}
						}
					</div>
				</section>
			}

			<mat-divider></mat-divider>
			<h2>{{ 'user.assignment_transfer.completed_assignments' | transloco }}</h2>

			@for (c of finishedSectionClients(); track c.clientId) {
				<section class="client-block">
					<h3>{{ c.clientFullName }}</h3>

					@for (a of c.finished; track a.id) {
						<lvr-user-client-assignment-item
							[clientId]="c.clientId"
							[assignment]="a"
							variant="finished"
							[showCheckbox]="false"
						/>
					}
				</section>
			}
		</div>

		<lvr-paginator
			[length]="totalItems()"
			[pageSize]="pageSize()"
			[pageIndex]="pageIndex()"
			[showFirstLastButtons]="true"
			[hidePageSize]="false"
			preferenceKey="user-assignment-transfer"
			(page)="handlePageEvent($event)"
		>
		</lvr-paginator>
	}

	<lvr-default-actions
		lvrPadding
		[padHorizontal]="32"
		[disableSave]="!canSave()"
		(save)="save()"
		[disableCancelSave]="saving()"
		(cancelSave)="cancel()"
		[showLoading]="saving()"
	></lvr-default-actions>
</section>
